import GeoJSON from 'geojson'

import { AnyAirspace } from '@/domain/models/Airspace'
import { AirspaceType } from '@/domain/protocols/Airspaces'

import { AirspaceFeature } from './IAirspacesRepository'

// `type` do FlatRecord (snake_case) -> chave do app (camelCase). Tipos ausentes
// aqui ficam com a string crua e são renderizados genericamente (ver
// airspaceTypeRegistry).
const FRB_TYPE_TO_KEY: Record<string, AirspaceType> = {
  adiz: 'adiz',
  afis: 'afis',
  atz: 'atz',
  cta: 'cta',
  cta_sector: 'ctaSector',
  ctr: 'ctr',
  fca: 'fca',
  fca_ad: 'fcaAd',
  fca_reh: 'fcaReh',
  fir: 'fir',
  fir_sector: 'firSector',
  fis: 'fis',
  fiz: 'fiz',
  free_route_areas: 'freeRouteAreas',
  sua_dangerous: 'suaDangerous',
  sua_prohibited: 'suaProhibited',
  sua_restricted: 'suaRestricted',
  tma: 'tma',
  tma_sector: 'tmaSector'
}

export const resolveAirspaceTypeKey = (rawType: string): string => FRB_TYPE_TO_KEY[rawType] ?? rawType

const toTrimmedString = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined
  const str = String(value).trim()
  return str.length > 0 ? str : undefined
}

const toNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

// O payload legado do Geoserver trazia um `localLangCode` por feature; o FRB traz
// uma tag `*_lang` por campo localizado. São consistentes dentro de uma feature,
// então a primeira tag presente é representativa — o painel de detalhe só a usa
// para decidir entre texto localizado e em inglês.
const LANG_FIELDS = [
  'class_text_lang',
  'comms_text_lang',
  'lower_limit_text_lang',
  'upper_limit_text_lang',
  'operational_hours_lang',
  'remarks_lang'
]

// Default 'pt' quando nenhuma tag está presente: é o dataset brasileiro e o
// payload legado do Geoserver definia localLangCode='pt', então o painel segue
// mostrando o texto localizado (não o inglês) em vez de regredir com tags esparsas.
const resolveLocalLangCode = (properties: Record<string, unknown>): string => {
  for (const field of LANG_FIELDS) {
    const lang = toTrimmedString(properties[field])
    if (lang) return lang.slice(0, 2).toLowerCase()
  }
  return 'pt'
}

// Desce em toda posição aninhada de um Polygon/MultiPolygon e cresce a caixa. O
// FRB não traz bbox por feature; o mapa a usa para enquadrar o airspace selecionado.
const growBbox = (node: unknown, box: [number, number, number, number]): void => {
  if (!Array.isArray(node)) return
  if (typeof node[0] === 'number' && typeof node[1] === 'number') {
    const [lng, lat] = node as number[]
    if (lng < box[0]) box[0] = lng
    if (lat < box[1]) box[1] = lat
    if (lng > box[2]) box[2] = lng
    if (lat > box[3]) box[3] = lat
    return
  }
  for (const child of node) growBbox(child, box)
}

const computeBbox = (geometry: GeoJSON.Geometry): GeoJSON.BBox | undefined => {
  if (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') return undefined
  const box: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity]
  growBbox(geometry.coordinates, box)
  return Number.isFinite(box[0]) ? box : undefined
}

/**
 * Mapeia uma feature de airspace do FlatRecord para o shape de feature legado do
 * Geoserver que a UI já consome (props camelCase, `localLangCode`, `bbox` por
 * feature), devolvendo a chave de tipo resolvida ao lado dela. `null` quando a
 * geometria/tipo é inutilizável, para que um único registro ruim não quebre uma
 * camada inteira.
 */
export const mapAirspaceFeature = (feature: GeoJSON.Feature): { typeKey: string; feature: AirspaceFeature } | null => {
  try {
    const geometry = feature.geometry
    if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) return null

    const p = (feature.properties ?? {}) as Record<string, unknown>
    const rawType = toTrimmedString(p.type)
    if (!rawType) return null

    const properties = {
      code: toTrimmedString(p.code),
      name: toTrimmedString(p.name),
      localLangCode: resolveLocalLangCode(p),
      class: toTrimmedString(p.class_text),
      classEng: toTrimmedString(p.class_text_en),
      lowerLimit: toNumber(p.lower_limit_ft),
      lowerLimitTxt: toTrimmedString(p.lower_limit_text),
      lowerLimitTxtEng: toTrimmedString(p.lower_limit_text_en),
      upperLimit: toNumber(p.upper_limit_ft),
      upperLimitTxt: toTrimmedString(p.upper_limit_text),
      upperLimitTxtEng: toTrimmedString(p.upper_limit_text_en),
      oprHours: toTrimmedString(p.operational_hours),
      oprHoursEng: toTrimmedString(p.operational_hours_en),
      rmk: toTrimmedString(p.remarks),
      rmkEng: toTrimmedString(p.remarks_en),
      commTxt: toTrimmedString(p.comms_text),
      commTxtEng: toTrimmedString(p.comms_text_en),
      labelPosLng: toNumber(p.label_lon),
      labelPosLat: toNumber(p.label_lat)
    } as AnyAirspace

    const mapped: AirspaceFeature = {
      type: 'Feature',
      id: feature.id,
      bbox: computeBbox(geometry),
      geometry: geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon,
      properties
    }

    return { typeKey: resolveAirspaceTypeKey(rawType), feature: mapped }
  } catch (error) {
    console.error(`[AirspaceFlatRecordMapper] ${error}`)
    return null
  }
}
